import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Check, Loader2, X, XCircle } from 'lucide-react'
import { useConsoleState } from '@/lib/voice-console/state/context'
import type { ActiveTool } from '@/lib/voice-console/state/types'
import { getToolVisual } from '@/lib/voice-console/tool-icons'
import { cn } from '@/lib/utils'

const COMPLETED_VISIBILITY_MS = 3000
const MAX_VISIBLE_TOOLS = 3
const SWIPE_DISMISS_THRESHOLD = 72 // px

function getProgressPercent(step: number, ofSteps: number | null): number | null {
  if (ofSteps === null || ofSteps <= 0) return null
  return Math.max(0, Math.min(100, (step / ofSteps) * 100))
}

function statusLabel(tool: ActiveTool): string {
  if (tool.status === 'completed') return 'Completed'
  if (tool.status === 'failed') return 'Failed'
  if (tool.status === 'running' && tool.step_label) return tool.step_label
  if (tool.of_steps === null) return 'Running'
  return `${tool.step}/${tool.of_steps}`
}

function ToolCard({
  tool,
  now,
  onDismiss,
}: {
  tool: ActiveTool
  now: number
  onDismiss?: () => void
}) {
  const visual = getToolVisual(tool.tool)
  const isRunning = tool.status === 'running'
  const isFailed = tool.status === 'failed'
  const isCompleted = tool.status === 'completed'
  const progressPercent = getProgressPercent(tool.step, tool.of_steps)
  const completedAge = isCompleted && tool.completedAt ? now - tool.completedAt : 0
  const completedOpacity = isCompleted
    ? Math.max(0.2, 1 - (completedAge / COMPLETED_VISIBILITY_MS) * 0.9)
    : 1

  // Swipe-to-dismiss state (failed cards only)
  const [dragX, setDragX] = useState(0)
  const dragStartX = useRef<number | null>(null)
  const isDragging = useRef(false)

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isFailed || !onDismiss) return
      dragStartX.current = e.clientX
      isDragging.current = false
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    },
    [isFailed, onDismiss]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (dragStartX.current === null || !isFailed || !onDismiss) return
      const delta = e.clientX - dragStartX.current
      if (delta < -4) {
        isDragging.current = true
        setDragX(Math.max(delta, -140))
      }
    },
    [isFailed, onDismiss]
  )

  const handlePointerUp = useCallback(() => {
    if (dragX < -SWIPE_DISMISS_THRESHOLD && onDismiss) {
      onDismiss()
    } else {
      setDragX(0)
    }
    dragStartX.current = null
  }, [dragX, onDismiss])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Block click-through if the pointer was a swipe
      if (isDragging.current) e.stopPropagation()
    },
    []
  )

  const swipeRevealWidth = Math.min(Math.abs(dragX), 60)
  const swipeOpacity = Math.min(Math.abs(dragX) / SWIPE_DISMISS_THRESHOLD, 1)

  return (
    <div
      className="relative overflow-hidden rounded-lg"
      onClick={handleClick}
    >
      {/* Red swipe reveal behind the card */}
      {isFailed && (
        <div
          className="absolute inset-y-0 right-0 flex items-center justify-center bg-red-600/80 rounded-lg"
          style={{ width: swipeRevealWidth, opacity: swipeOpacity }}
        >
          <X className="h-4 w-4 text-white" />
        </div>
      )}

      <div
        className={cn(
          'rounded-lg border px-3 py-2.5 transition-[opacity] select-none',
          isRunning && 'border-violet-700/70 bg-violet-950/35',
          isCompleted && 'border-emerald-700/40 bg-emerald-950/20',
          isFailed && 'border-red-700/60 bg-red-950/30'
        )}
        style={{
          opacity: completedOpacity,
          transform: `translateX(${dragX}px)`,
          transition: dragX === 0 && !isDragging.current ? 'transform 0.2s ease-out' : 'none',
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <div className="flex items-start gap-2">
          <visual.Icon className={cn('mt-0.5 h-4 w-4 shrink-0', visual.accentClass)} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <span className="truncate text-xs font-semibold text-slate-200">{visual.label}</span>
              <div className="flex items-center gap-1 shrink-0">
                <span
                  className={cn(
                    'text-[11px]',
                    isRunning && 'text-violet-300',
                    isCompleted && 'text-emerald-300',
                    isFailed && 'text-red-300'
                  )}
                >
                  {statusLabel(tool)}
                </span>
                {isFailed && onDismiss && (
                  <button
                    className="ml-0.5 rounded p-0.5 text-red-400 hover:bg-red-900/50 hover:text-red-200 active:scale-90 transition-all"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => { e.stopPropagation(); onDismiss() }}
                    aria-label="Dismiss"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>

            {isRunning && progressPercent !== null && (
              <div className="mt-1.5">
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800/90">
                  <div
                    className="h-full rounded-full bg-violet-400 transition-all duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                {tool.step_label && (
                  <p className="mt-1 text-[11px] text-violet-300 truncate">{tool.step_label}</p>
                )}
              </div>
            )}

            {isRunning && progressPercent === null && (
              <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-violet-300">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>{tool.step_label ?? 'Working'}</span>
              </div>
            )}

            {isCompleted && (
              <div className="mt-1.5 flex items-center gap-1 text-[11px] text-emerald-300">
                <Check className="h-3 w-3" />
                <span>Done</span>
              </div>
            )}

            {isFailed && (
              <div
                className="mt-1.5 flex items-center gap-1 text-[11px] text-red-400 cursor-help"
                title={tool.error}
              >
                <XCircle className="h-3 w-3 shrink-0" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function OperationsFloor() {
  const { state, dispatch } = useConsoleState()
  const { activeTools, assistantStatus } = state
  const [now, setNow] = useState(() => Date.now())

  const hasToolAnimation = activeTools.some((tool) => {
    if (tool.status === 'running') return true
    if (tool.status === 'completed' && tool.completedAt) {
      return now - tool.completedAt < COMPLETED_VISIBILITY_MS
    }
    return false
  })

  const shouldTick = hasToolAnimation

  useEffect(() => {
    if (!shouldTick) return
    const intervalId = window.setInterval(() => setNow(Date.now()), 250)
    return () => window.clearInterval(intervalId)
  }, [shouldTick])

  const visibleTools = useMemo(() => {
    const filteredTools = activeTools.filter((tool) => {
      if (tool.status !== 'completed' || !tool.completedAt) return true
      return now - tool.completedAt < COMPLETED_VISIBILITY_MS
    })
    return filteredTools.slice(-MAX_VISIBLE_TOOLS)
  }, [activeTools, now])

  const hasActiveTools = visibleTools.some((tool) => tool.status === 'running')

  if (visibleTools.length === 0) {
    if (assistantStatus !== 'tool_use') return null
    return (
      <div className="flex items-center justify-center px-4 py-3 shrink-0">
        <span className="text-xs text-violet-400 animate-pulse">Working…</span>
      </div>
    )
  }

  return (
    <div className="shrink-0 border-t border-slate-800/60 px-4 py-3">
      {visibleTools.length > 0 && (
        <div className="grid max-h-[30svh] gap-2 overflow-y-auto pr-1">
          {visibleTools.map((tool) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              now={now}
              onDismiss={
                tool.status === 'failed'
                  ? () => dispatch({ type: 'TOOL_DISMISSED', id: tool.id })
                  : undefined
              }
            />
          ))}
        </div>
      )}

      {hasActiveTools && <p className="mt-1.5 text-xs text-slate-500">Tools running…</p>}
    </div>
  )
}
